'use client'

import { useState, useCallback } from 'react'
import dynamic from 'next/dynamic'
import { useForm } from 'react-hook-form'
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid } from '@mui/material'
import { useSnackbar } from 'notistack'
import Turnstile, { useTurnstile } from 'react-turnstile'
import { useCookies } from 'react-cookie'

// Types
import type { FieldValues } from 'react-hook-form'
import type { TextFieldProps } from '@/types/components/molecules/inputs/text'
import type { APIFormSubmissionCreateResult } from '@/types/api/(builder)/form-submission/create'
import type { ModalFormProps } from '@/types/components/organisms/forms/modal'

// Configurations
import Config from '@/config'
import FormConfig from '@/config/form'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

// Components
const TextFieldMolecule = dynamic(() => import('@/components/molecules/fields/text'))
const AlertItemAtom = dynamic(() => import('@/components/atoms/items/alert'))
const MainButtonAtom = dynamic(() => import('@/components/atoms/buttons/main'))

const ModalFormOrganism = (props: ModalFormProps) => {
	// Props
	const { lng, form, open, setOpen } = props

	// Variables
	const { t } = useTranslation()
	const { enqueueSnackbar } = useSnackbar()
	const turnstile = useTurnstile()
	const [cookies, setCookie] = useCookies([`${Config.shortName}_form_${form.alias}`])
	const [loading, setLoading] = useState<boolean>(false)
	const [token, setToken] = useState<string>('')

	// Cookies
	const formSubmitted = cookies[`${Config.shortName}_form_${form.alias}`]
	const checkFormSubmitted = formSubmitted ? new Date(formSubmitted).getTime() > new Date().getTime() : false

	// Form
	const { control, formState, handleSubmit, reset } = useForm<FieldValues>({
		mode: 'onChange',
		defaultValues: form.field_category?.items.reduce(
			(acc, field) => {
				acc[field.field.alias] = ''

				return acc
			},
			{} as Record<string, string>
		)
	})

	const errors = formState?.errors ?? {}

	// Callbacks
	const onSubmit = useCallback(
		async (values: FieldValues) => {
			// Set loading enabled
			setLoading(true)

			// Response
			const res = await fetch(`/api/form-submission/create?turnstile_token=${token}&form_alias=${form.alias}`, {
				method: 'PUT',
				headers: { 'Accept-Language': lng, 'Content-Type': 'application/json' },
				body: JSON.stringify({
					fields_values: JSON.stringify(Object.keys(values).map((index: string) => ({ [index]: values[index] })))
				})
			})

			// Result
			const data: APIFormSubmissionCreateResult = await res.json()

			// Set loading disabled
			setLoading(false)

			// Check result
			if (data.status) {
				reset()
				setOpen(false)

				// Set cookie
				const expires = new Date(new Date().setMinutes(new Date().getMinutes() + 5))

				setCookie(`${Config.shortName}_form_${form.alias}`, expires.toISOString(), { path: '/', maxAge: 300, expires })
			}

			// Check messages
			if (data.message) enqueueSnackbar(data.message, { variant: data.status ? 'success' : 'error', autoHideDuration: 6000 })
			if (data.messages) data.messages.map(message => enqueueSnackbar(message, { variant: data.status ? 'success' : 'error', autoHideDuration: 6000 }))

			// Reset turnstile
			turnstile.reset()
			setToken('')
		},
		[lng, form, token, turnstile, reset, setCookie, setOpen, enqueueSnackbar, setToken]
	)

	return (
		<Dialog component="form" open={open} onClose={() => setOpen(false)} onSubmit={handleSubmit(onSubmit)}>
			<DialogTitle>{form.title}</DialogTitle>

			<DialogContent>
				{!checkFormSubmitted && (
					<Grid container spacing={1} mt={1}>
						{form.field_category?.items.map((field, index) => {
							const fieldItem = field.field
							const fieldType = fieldItem.type_name as TextFieldProps['type']
							const attributes = fieldItem.attributes
							let required: boolean = false
							let multiline: boolean = false

							// Check attributes
							if (attributes.length) {
								const requiredAttribute = attributes.find(attribute => attribute.name === 'required')
								const multilineAttribute = attributes.find(attribute => attribute.name === 'multiline')

								if (requiredAttribute) required = requiredAttribute.value === '1' || requiredAttribute.value === 'true'
								if (multilineAttribute) multiline = multilineAttribute.value === '1' || multilineAttribute.value === 'true'
							}

							if (fieldType && ['text', 'email'].includes(fieldType))
								return (
									<Grid key={index} size={12}>
										<TextFieldMolecule
											lng={lng}
											control={control}
											name={field.field.alias}
											type={fieldType}
											label={field.label}
											multiline={multiline}
											rows={multiline ? 4 : undefined}
											pattern={fieldType === 'email' ? 'email' : undefined}
											helperText="form:helper.error"
											required={required}
										/>
									</Grid>
								)
						})}

						<Grid key="turnstile" size={12}>
							<Turnstile sitekey={FormConfig.site_key} onVerify={token => setToken(token)} />
						</Grid>
					</Grid>
				)}

				{checkFormSubmitted && (
					<Grid container spacing={1} mt={1}>
						<Grid size={12}>
							<AlertItemAtom variant="outlined" severity="success">
								{t('form:message.success')}
							</AlertItemAtom>
						</Grid>
					</Grid>
				)}
			</DialogContent>

			<DialogActions>
				<MainButtonAtom lng={lng} title="form:button.cancel" onClick={() => setOpen(!open)} />

				{!checkFormSubmitted && (
					<MainButtonAtom
						lng={lng}
						title="form:button.send"
						type="submit"
						loading={loading}
						disabled={Object.keys(errors).length > 0 || !token}
						onClick={handleSubmit(onSubmit)}
					/>
				)}
			</DialogActions>
		</Dialog>
	)
}

export default ModalFormOrganism
